import React, { FC, memo } from "react";
import { ClusterSymbolPreview, SvgPointSymbol, SvgSymbol } from "@evergis/react";
import { ClusterSymbolDc, GeometryType } from "@evergis/api";
import { IconButton, Icon } from "@evergis/ui";

import { CompoundIcon } from "../../modules/LayerList/components/CompoundIcon";
import { Hexagon } from "./components/Hexagon";
import { Square } from "./components/Square";
import {
  isClusterSymbol,
  isCombinedPolylineSymbol,
  isH3GridSymbol,
  isSvgPointSymbol,
  ParameterValue,
} from "../../interfaces/style";
import { images } from "../../modules/SymbolLibrary/images";
import { DEFAULT_SYMBOL_WITH_BG_SIZE, MAX_SYMBOL_SIZE } from "../../modules/SymbolLibrary/constants";
import { getSymbol, getSymbolWithError } from "../../react-sgis/symbols/helpers";
import { getGeomIcon } from "../Dialogs/DialogComponents/TableSourceDescription";
import { deserializeSymbol } from "../../react-sgis/symbols/SymbolRenderer/deserializeSymbol";
import { getExtractedSymbol } from "../../utils/style";
import { getParameterValue, isParameterByAttribute } from "../../evergis/core/classification/getFromClassified";
import { StyleIconProps } from "./types";

export const StyleIcon: FC<StyleIconProps> = memo(
  ({ symbol, style, className, hasClassification, hasRasters, error, type, geometryType, sourceType, warn }) => {
    const isNoGeometryLayer = geometryType === GeometryType.Unknown;
    const isCluster = style && isClusterSymbol(style.symbol);
    const isClusterWithCountClassify =
      isCluster && !!style.classificationManager.find(({ attribute }) => attribute === "count");

    if (error) {
      return <IconButton kind={"warning"} error />;
    }

    if (sourceType) {
      return <Icon kind={"externalservice"} style={{ margin: "auto" }} primary />;
    }

    if (type === "RemoteTileService") {
      return <Icon kind={"tms"} style={{ margin: "auto", color: "#9A5BEB" }} />;
    }

    if (isNoGeometryLayer) {
      return <Icon kind={"geom_unknown"} style={{ margin: "auto" }} />;
    }

    if ((!symbol || !deserializeSymbol(symbol)) && geometryType) {
      return <Icon kind={getGeomIcon(geometryType)} style={{ margin: "auto" }} />;
    }

    if (!symbol) {
      return null;
    }

    if (isClusterWithCountClassify) {
      return <ClusterSymbolPreview symbol={symbol as ClusterSymbolDc} size={MAX_SYMBOL_SIZE} fontSize={"0.75rem"} />;
    }

    if (isCluster) {
      const extracted = getExtractedSymbol(symbol, true) as SvgPointSymbol;

      return (
        <SvgSymbol
          symbol={{ ...extracted, size: DEFAULT_SYMBOL_WITH_BG_SIZE }}
          svg={getParameterValue(extracted.data as ParameterValue) as string}
          skipOffset
        />
      );
    }

    if (isH3GridSymbol(symbol)) {
      if (!isParameterByAttribute(symbol.fill.color)) {
        return <img src={images.defaultH3Symbol} alt="H3" style={{ width: "1.5rem", alignSelf: "center" }} />;
      }

      return <Hexagon symbol={symbol} className={className} style={{ width: "2rem" }} />;
    }

    if (isSvgPointSymbol(symbol)) {
      return (
        <SvgSymbol
          symbol={{ ...symbol, size: DEFAULT_SYMBOL_WITH_BG_SIZE } as SvgPointSymbol}
          svg={getParameterValue(symbol.data as ParameterValue) as string}
          size={getParameterValue(symbol.size as ParameterValue) as number}
          skipOffset
        />
      );
    }

    // Render polygon with dynamic gradient from classification colors
    if (geometryType === GeometryType.Polygon) {
      return <Square symbol={symbol} className={className} />;
    }

    // Fallback to static compound icon for other geometries with classification
    if (hasClassification && !isCombinedPolylineSymbol(symbol)) {
      return <CompoundIcon geometryType={geometryType} />;
    }

    if ((warn && (geometryType || symbol)) || (!geometryType && symbol)) {
      return getSymbolWithError(geometryType || symbol);
    }

    if (hasRasters) {
      return <Icon kind={getGeomIcon("raster")} style={{ margin: "auto" }} />;
    }

    return getSymbol(symbol);
  },
);
